import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { auth } from "../lib/api";
import { authExtras } from "../lib/portalApi";
import { toast } from "sonner";
import { ShieldCheck, Key } from "lucide-react";

const PortalLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await auth.login(email, password);
      auth.saveToken(r.access_token);
      toast.success(`Welcome, ${r.user.name}`);
      // Role-based redirect (per spec)
      const role = r.user?.role;
      const SPORTS_ROLES = new Set([
        "athletic_director", "head_coach", "asst_coach", "position_coach",
        "strength_coach", "trainer_health", "compliance_officer",
        "academic_advisor", "recruiting_coordinator", "athlete",
        "parent_guardian", "sponsor_viewer", "read_only_auditor", "university_admin",
      ]);
      if (role === "super_admin")           nav("/portal/admin");
      else if (role === "admin")            nav("/admin");
      else if (role === "student")          nav("/portal/student");
      else if (role === "teacher_aide")     nav("/portal/teacher-aide");
      else if (role === "parent")           nav("/portal/parent");
      else if (SPORTS_ROLES.has(role))      nav("/auditiq-sports/dashboard");
      else                                  nav("/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    }
    setBusy(false);
  };

  const requestReset = async () => {
    if (!email) return toast.error("Enter your email first");
    try {
      const r = await authExtras.passwordResetRequest(email);
      if (r.token) {
        setResetToken(r.token);
        toast.success("Reset token issued (demo mode) — token filled in.");
      } else toast.success(r.message || "If the email exists, a reset link has been issued.");
      setResetMode(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (!resetToken || !newPassword) return toast.error("Token and new password are required");
    try {
      await authExtras.passwordReset(resetToken, newPassword);
      toast.success("Password updated — signing you in.");
      setPassword(newPassword);
      setResetMode(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
  };

  return (
    <Layout>
      <SEOHead title="Secure Portal Login | AuditIQ" path="/portal/login" />
      <div className="flex justify-center pt-12 pb-2">
        <img src="/images/auditiq-logo-white-medium.png" alt="AuditIQ" style={{ height: 64, width: "auto" }} data-testid="portal-login-logo" />
      </div>
      <PageHeader eyebrow="Secure Portal" title="Sign in to AuditIQ"
        subtitle="Super Admin, Sales Demo, and Municipality access. Sales Demo accounts have full system access at no cost." />

      <section className="max-w-[560px] mx-auto px-6 py-14">
        <div className="glass p-8">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-electric">
            <ShieldCheck size={14} /> Secure Login
          </div>
          <h2 className="font-display text-2xl mt-2">Authenticate</h2>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="portal-login-form">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Email</label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-navy-deep border-gold-dim/40" data-testid="portal-email" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Password</label>
              <Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 bg-navy-deep border-gold-dim/40" data-testid="portal-password" />
            </div>
            <Button disabled={busy} className="btn-gold h-11 w-full" data-testid="portal-login-btn">
              {busy ? "Signing in…" : "Sign In Securely"}
            </Button>
            <div className="flex justify-end text-xs">
              <button type="button" onClick={requestReset} className="text-electric hover:text-gold" data-testid="portal-forgot-btn">
                Forgot password?
              </button>
            </div>
          </form>

          {resetMode && (
            <form onSubmit={confirmReset} className="mt-6 pt-6 border-t border-gold-dim/20 space-y-3" data-testid="portal-reset-form">
              <div className="text-[11px] uppercase tracking-[0.22em] text-electric flex items-center gap-2">
                <Key size={12} /> Reset Password
              </div>
              <Input value={resetToken} onChange={(e) => setResetToken(e.target.value)} placeholder="Reset token"
                className="bg-navy-deep border-gold-dim/40 font-mono text-xs" data-testid="portal-reset-token" />
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password (min 6 chars)" className="bg-navy-deep border-gold-dim/40" data-testid="portal-new-password" />
              <Button className="btn-gold h-10 w-full" data-testid="portal-reset-confirm">Update Password</Button>
            </form>
          )}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/admin/login"
            className="text-[11px] text-steel/70 hover:text-gold transition-colors uppercase tracking-[0.2em]"
            data-testid="portal-login-admin-access-link"
          >
            Admin Access
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default PortalLogin;
